import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import type { OversightDashboardDateField } from '@risksmart-app/domain/src/types/consts/dashboard-date-fields';
import type { OversightRegisterResponse } from '@risksmart-app/trpc/src/types';
import type {
  GetOversightAssessmentsQuery,
  Oversight_Assessment_Bool_Exp,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetOversightAssessmentsDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { createQueryHook, type StrictMappedGraphQLSelection } from 'src/utils';

import { OVERSIGHT_DATE_PREDICATES } from '@/hooks/queries/dashboardDatePredicates';
import {
  buildDashboardWhere,
  type DashboardFilterArgs,
  departmentsWhereOnEntity,
  tagsWhereOnEntity,
  toTrpcDashboardFilterInput,
} from '@/hooks/queries/dashboardFilterArgs';

/**
 * Reshapes the register response into the GraphQL query shape so both branches
 * feed consumers identical data.
 *
 * The GraphQL selects `oversightResults` as `assessedItems`, with each leg
 * reached through a `parents` array filtered by ParentType. Drizzle cannot
 * alias one relation twice, so the register query config selects `risk` /
 * `obligation` / `document` directly alongside the result rows, and the
 * one-element `parents` arrays are synthesised here.
 *
 * All six assessed-item legs are reconstructed — controlled + uncontrolled
 * risk, obligation, document, control test and impact — to match what the
 * GraphQL leg returns; omitting any of them blanked those items in the
 * "Assessed items" column for orgs on the tRPC path.
 *
 * `assessmentResults` is destructured out rather than spread through, so the
 * mapped row carries only `assessedItems` — the field the GraphQL query
 * actually returns. Leaving both on the object would make the two branches
 * observably different, which defeats the point of the bridge.
 */
export const mapOversightRegisterToGraphQL = (
  data: OversightRegisterResponse
): StrictMappedGraphQLSelection<GetOversightAssessmentsQuery> => ({
  oversight_assessment: data.oversight.map(
    ({ assessmentResults, ...item }) => ({
      ...item,
      assessedItems: assessmentResults.map((ar) => ({
        controlledRiskAssessmentResult: ar.controlledRiskAssessmentResult
          ? {
              Id: ar.controlledRiskAssessmentResult.Id,
              parents: [{ risk: ar.risk }],
            }
          : null,
        uncontrolledRiskAssessmentResult: ar.uncontrolledRiskAssessmentResult
          ? {
              Id: ar.uncontrolledRiskAssessmentResult.Id,
              parents: [{ risk: ar.risk }],
            }
          : null,
        obligationAssessmentResult: ar.obligationAssessmentResult
          ? {
              Id: ar.obligationAssessmentResult.Id,
              parents: [{ obligation: ar.obligation }],
            }
          : null,
        documentAssessmentResult: ar.documentAssessmentResult
          ? {
              Id: ar.documentAssessmentResult.Id,
              parents: [{ document: ar.document }],
            }
          : null,
        testResult: ar.testResult
          ? {
              Id: ar.testResult.Id,
              parent: ar.testResult.parent,
            }
          : null,
        impactRating: ar.impactRating
          ? {
              Id: ar.impactRating.Id,
              impact: ar.impactRating.impact,
            }
          : null,
      })),
    })
  ),
});

/**
 * COI-691 — `getOversightAssessments` took no variables at all until this
 * change, so the widget's `where` fragments were silently discarded on the
 * Apollo leg. The document now accepts `$where` (defaulted `{}`), which is what
 * makes this builder reachable. Ownership reuses the assessment fragment: the
 * `oversight_assessment` view exposes the same owner / contributor /
 * ancestorContributor relations.
 */
const useOversightRegisterGraphqlVariables = (
  args: UseGetOversightAssessmentsRegisterArgs
) => {
  const { user } = useRisksmartUser();

  // Explicit type arguments: unlike the other five hooks, oversight has no
  // `base` (its own predicate is `undefined`, per the comment above), so
  // there is nothing to anchor `TWhere` for inference — every other argument
  // only *returns* it, which TypeScript can't unify across without an anchor.
  // `DashboardFilterArgs<...>` is the same exported type
  // `UseGetOversightAssessmentsRegisterArgs` aliases below, not a one-off
  // shape, so this isn't a cast — just spelling out what would otherwise be
  // inferred.
  const { where } = buildDashboardWhere<
    DashboardFilterArgs<OversightDashboardDateField>,
    Oversight_Assessment_Bool_Exp
  >({
    filters: args,
    userId: user?.userId,
    datePredicates: OVERSIGHT_DATE_PREDICATES,
    tagsWhere: tagsWhereOnEntity,
    departmentsWhere: departmentsWhereOnEntity,
    // Ownership reuses the assessment fragment: the `oversight_assessment`
    // view exposes the same owner / contributor / ancestorContributor
    // relations as `assessment`.
    ownershipWhere: (conditions) =>
      conditions.assessmentFilterConditions as Oversight_Assessment_Bool_Exp,
  });

  return where ? { where } : {};
};

export type UseGetOversightAssessmentsRegisterArgs =
  DashboardFilterArgs<OversightDashboardDateField>;

/**
 * The oversight assessment register read.
 *
 * Named `...Register` after `useGetInternalAuditReportsRegister`, and
 * deliberately NOT `useGetOversightAssessments` — `useGetAllOversightAssessments`
 * already exists and wraps `frontend.oversight.getAll`, a much thinner flat
 * column subset with no relations.
 */
export const useGetOversightAssessmentsRegister = createQueryHook<
  UseGetOversightAssessmentsRegisterArgs,
  OversightRegisterResponse,
  GetOversightAssessmentsQuery
>({
  trpcQueryOptions: (
    trpc,
    { tagTypeIds, departmentTypeIds, dateRange, ownership }
  ) =>
    trpc.frontend.oversight.register.queryOptions(
      toTrpcDashboardFilterInput({
        tagTypeIds,
        departmentTypeIds,
        dateRange,
        ownership,
      })
    ),
  mapTrpcDataToGraphQL: mapOversightRegisterToGraphQL,
  graphqlDocument: GetOversightAssessmentsDocument,
  graphqlVariables: useOversightRegisterGraphqlVariables,
});
